/**
 * Exit Tracker Page - Phase 6
 * Decision-support system for optimizing sale process, pricing strategy, and final net outcome
 * All calculations come from backend - NO duplicated frontend logic
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  TrendDown,
  ChartLine,
  Target,
  Clock,
  CurrencyEur,
  House,
  CheckCircle,
  Warning,
  CaretRight,
  ArrowsClockwise,
  Plus,
  Eye,
  Handshake,
  X,
  Lightning,
  Scales,
  Timer,
} from '@phosphor-icons/react';
import { LoadingPage, EmptyState } from '../components/common';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { exitTrackerApi, offersApi, dealsApi } from '../utils/api';
import {
  formatCurrency,
  formatPricePerSqm,
  formatDate,
} from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Status badge colors
const STATUS_COLORS = {
  not_listed: 'bg-stone-100 text-stone-700',
  listed: 'bg-blue-100 text-blue-700',
  under_offer: 'bg-amber-100 text-amber-700',
  sold: 'bg-emerald-100 text-emerald-700',
};

const URGENCY_COLORS = {
  low: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const RECOMMENDATION_COLORS = {
  accept: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  negotiate: 'bg-amber-100 text-amber-700 border-amber-300',
  reject: 'bg-red-100 text-red-700 border-red-300',
  wait: 'bg-blue-100 text-blue-700 border-blue-300',
};

export const ExitTracker = () => {
  const { dealId } = useParams();
  const [searchParams] = useSearchParams();
  const dealIdFromUrl = searchParams.get('deal') || dealId;
  
  const [loading, setLoading] = useState(true);
  const [tracker, setTracker] = useState(null);
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(dealIdFromUrl || null);
  const [error, setError] = useState(null);
  
  // Modals
  const [showAddOffer, setShowAddOffer] = useState(false);
  const [showPriceReduction, setShowPriceReduction] = useState(false);
  const [showListingSetup, setShowListingSetup] = useState(false);
  
  // Form states
  const [offerForm, setOfferForm] = useState({
    offer_price: '',
    buyer_type: 'private',
    conditions: 'none',
    probability_of_closing: 70,
    expected_days_to_close: 60,
    buyer_financing_status: 'pre_approved',
    notes: '',
  });
  const [priceReductionForm, setPriceReductionForm] = useState({
    new_price: '',
    reason: 'market_feedback',
  });
  const [listingForm, setListingForm] = useState({
    listing_status: 'listed',
    listing_start_date: new Date().toISOString().split('T')[0],
    listing_price: '',
    minimum_acceptable_price: '',
    marketing_channel: 'agency',
    agent_name: '',
    agency_name: '',
    staging_quality: 'standard',
  });
  const [saving, setSaving] = useState(false);

  // Fetch deals for selector
  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const data = await dealsApi.getSummaries();
        const exitDeals = data.filter(d => 
          ['acquired', 'renovation', 'ready_for_sale', 'sold'].includes(d.status)
        );
        setDeals(exitDeals);
        
        if (!selectedDealId && exitDeals.length > 0) {
          setSelectedDealId(exitDeals[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch deals:', err);
      }
    };
    fetchDeals();
  }, [selectedDealId]);

  // Fetch tracker data
  const fetchTracker = useCallback(async () => {
    if (!selectedDealId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await exitTrackerApi.getTracker(selectedDealId);
      setTracker(data);
      
      if (data) {
        setListingForm(prev => ({
          ...prev,
          listing_price: data.listing_price || data.target_exit_price || '',
          minimum_acceptable_price: data.minimum_acceptable_price || '',
        }));
      }
    } catch (err) {
      console.error('Failed to fetch exit tracker:', err);
      setError('Could not load exit tracker data');
    } finally {
      setLoading(false);
    }
  }, [selectedDealId]);

  useEffect(() => {
    fetchTracker();
  }, [fetchTracker]);

  // Handle offer submission
  const handleAddOffer = async () => {
    if (!offerForm.offer_price) {
      toast.error('Offer price is required');
      return;
    }
    
    try {
      setSaving(true);
      await offersApi.create(selectedDealId, {
        offer_price: parseFloat(offerForm.offer_price),
        buyer_type: offerForm.buyer_type,
        conditions: offerForm.conditions,
        probability_of_closing: offerForm.probability_of_closing,
        expected_days_to_close: offerForm.expected_days_to_close,
        buyer_financing_status: offerForm.buyer_financing_status,
        notes: offerForm.notes,
      });
      toast.success('Offre enregistrée');
      setShowAddOffer(false);
      setOfferForm({
        offer_price: '',
        buyer_type: 'private',
        conditions: 'none',
        probability_of_closing: 70,
        expected_days_to_close: 60,
        buyer_financing_status: 'pre_approved',
        notes: '',
      });
      fetchTracker();
    } catch (err) {
      toast.error('Échec de l\'ajout de l\'offre');
    } finally {
      setSaving(false);
    }
  };

  // Handle price reduction
  const handlePriceReduction = async () => {
    if (!priceReductionForm.new_price) {
      toast.error('Le nouveau prix est requis');
      return;
    }
    
    try {
      setSaving(true);
      await exitTrackerApi.recordPriceReduction(
        selectedDealId,
        parseFloat(priceReductionForm.new_price),
        priceReductionForm.reason
      );
      toast.success('Réduction de prix enregistrée');
      setShowPriceReduction(false);
      setPriceReductionForm({ new_price: '', reason: 'market_feedback' });
      fetchTracker();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de l\'enregistrement de la réduction');
    } finally {
      setSaving(false);
    }
  };

  // Handle listing setup
  const handleListingSetup = async () => {
    try {
      setSaving(true);
      await exitTrackerApi.updateListing(selectedDealId, listingForm);
      toast.success('Annonce mise à jour');
      setShowListingSetup(false);
      fetchTracker();
    } catch (err) {
      toast.error('Échec de la mise à jour de l\'annonce');
    } finally {
      setSaving(false);
    }
  };

  // Handle offer actions
  const handleAcceptOffer = async (offerId) => {
    if (!window.confirm('Accepter cette offre ? Les autres offres en attente seront rejetées.')) return;
    
    try {
      await offersApi.accept(selectedDealId, offerId);
      toast.success('Offre acceptée');
      fetchTracker();
    } catch (err) {
      toast.error('Échec de l\'acceptation de l\'offre');
    }
  };

  const handleRejectOffer = async (offerId) => {
    try {
      await offersApi.reject(selectedDealId, offerId);
      toast.success('Offre rejetée');
      fetchTracker();
    } catch (err) {
      toast.error('Échec du rejet de l\'offre');
    }
  };

  if (loading) {
    return <LoadingPage message="Chargement du suivi de sortie..." />;
  }

  if (!selectedDealId) {
    return (
      <div className="p-6">
        <EmptyState
          icon={House}
          title="Sélectionner un deal"
          description="Choisissez un deal pour voir le suivi de sortie."
        />
      </div>
    );
  }

  if (error || !tracker) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Warning}
          title="Erreur de chargement"
          description={error || "Impossible de charger les données de suivi."}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto" data-testid="exit-tracker-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1C2B24]">Exit Tracker</h1>
          <p className="text-muted-foreground">
            Decision support for {tracker.deal_name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedDealId} onValueChange={setSelectedDealId}>
            <SelectTrigger className="w-[280px]" data-testid="deal-selector">
              <SelectValue placeholder="Select deal" />
            </SelectTrigger>
            <SelectContent>
              {deals.map(d => (
                <SelectItem key={d.id} value={d.id}>
                  {d.deal_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {tracker.listing_status === 'not_listed' ? (
            <Button 
              className="bg-[#3A4A40] hover:bg-[#2E3B33]"
              onClick={() => setShowListingSetup(true)}
              data-testid="setup-listing-btn"
            >
              <Plus size={18} className="mr-1" />
              Setup Listing
            </Button>
          ) : (
            <Button 
              variant="outline"
              onClick={() => setShowPriceReduction(true)}
              data-testid="price-reduction-btn"
            >
              <TrendDown size={18} className="mr-1" />
              Reduce Price
            </Button>
          )}
          
          <Button 
            className="bg-[#3A4A40] hover:bg-[#2E3B33]"
            onClick={() => setShowAddOffer(true)}
            data-testid="add-offer-btn"
          >
            <Plus size={18} className="mr-1" />
            Ajouter Offre
          </Button>
        </div>
      </div>

      {/* Top Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Statut</p>
            <Badge className={cn('mt-1 capitalize', STATUS_COLORS[tracker.listing_status])}>
              {tracker.listing_status?.replace('_', ' ')}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Jours sur Marché</p>
            <p className="text-xl font-bold font-tabular">{tracker.days_on_market}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Prix Actuel</p>
            <p className="text-xl font-bold font-tabular">
              {formatCurrency(tracker.current_asking_price || tracker.target_exit_price)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Prix Cible</p>
            <p className="text-xl font-bold font-tabular">{formatCurrency(tracker.target_exit_price)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Minimum</p>
            <p className="text-xl font-bold font-tabular">{formatCurrency(tracker.minimum_acceptable_price)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Profit Net</p>
            <p className={cn(
              "text-xl font-bold font-tabular",
              tracker.net_profit_at_current_price >= 0 ? "text-emerald-600" : "text-red-600"
            )}>
              {formatCurrency(tracker.net_profit_at_current_price)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">ROI</p>
            <p className={cn(
              "text-xl font-bold",
              tracker.roi_at_current_price >= 15 ? "text-emerald-600" : 
              tracker.roi_at_current_price >= 10 ? "text-amber-600" : "text-red-600"
            )}>
              {tracker.roi_at_current_price?.toFixed(1)}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Urgence</p>
            <Badge className={cn('mt-1 capitalize', URGENCY_COLORS[tracker.time_impact?.urgency_level || 'low'])}>
              {tracker.time_impact?.urgency_level === 'high' ? 'Élevée' : 
               tracker.time_impact?.urgency_level === 'medium' ? 'Moyenne' : 'Basse'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Aperçu</TabsTrigger>
          <TabsTrigger value="offers">Offres ({tracker.offers_count})</TabsTrigger>
          <TabsTrigger value="pricing">Analyse Prix</TabsTrigger>
          <TabsTrigger value="timeline">Calendrier</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Recommendation Panel */}
          {tracker.recommendation && (
            <Card className="border-2 border-[#3A4A40] bg-[#F8FAF9]" data-testid="recommendation-panel">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Lightning size={20} className="text-[#3A4A40]" />
                  Action Recommandée
                </CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="text-xl font-bold text-[#1C2B24]">{tracker.recommendation.headline}</h3>
                <p className="text-muted-foreground mt-1">{tracker.recommendation.details}</p>
                
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-muted-foreground">Prix Optimal</p>
                    <p className="font-bold font-tabular">{formatCurrency(tracker.recommendation.optimal_listing_price)}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-muted-foreground">Minimum Acceptable</p>
                    <p className="font-bold font-tabular">{formatCurrency(tracker.recommendation.minimum_acceptable_price)}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs text-muted-foreground">Seuil Rentabilité</p>
                    <p className="font-bold font-tabular">{formatCurrency(tracker.recommendation.break_even_price)}</p>
                  </div>
                </div>
                
                {tracker.recommendation.action_items?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Actions à entreprendre:</p>
                    <ul className="space-y-1">
                      {tracker.recommendation.action_items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CaretRight size={14} className="mt-1 text-[#3A4A40]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Price Signals */}
          {tracker.price_signals?.length > 0 && (
            <Card data-testid="price-signals">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Warning size={18} />
                  Signaux d'Ajustement de Prix
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tracker.price_signals.map((signal, i) => (
                    <div 
                      key={i} 
                      className={cn(
                        "p-3 rounded-lg border-l-4",
                        signal.severity === 'critical' && "bg-red-50 border-red-500",
                        signal.severity === 'warning' && "bg-amber-50 border-amber-500",
                        signal.severity === 'info' && "bg-blue-50 border-blue-500"
                      )}
                    >
                      <p className="font-medium text-sm">{signal.message}</p>
                      <p className="text-sm text-muted-foreground mt-1">{signal.suggested_action}</p>
                      {signal.suggested_reduction_percent && (
                        <Badge variant="secondary" className="mt-2">
                          Suggéré: -{signal.suggested_reduction_percent}%
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Best Offer & Time Impact Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Best Offer */}
            <Card data-testid="best-offer-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Handshake size={18} />
                  Meilleure Offre Actuelle
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tracker.best_offer ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-3xl font-bold font-tabular">
                        {formatCurrency(tracker.best_offer.offer_price)}
                      </p>
                      <Badge 
                        className={cn(
                          "text-sm px-3 py-1",
                          RECOMMENDATION_COLORS[tracker.best_offer.recommendation]
                        )}
                      >
                        {tracker.best_offer.recommendation?.toUpperCase()}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Net Profit</p>
                        <p className={cn(
                          "font-bold font-tabular",
                          tracker.best_offer.net_profit >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {formatCurrency(tracker.best_offer.net_profit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">ROI</p>
                        <p className="font-bold">{tracker.best_offer.roi_percent?.toFixed(1)}%</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">vs Target</p>
                        <p className={cn(
                          "font-bold",
                          tracker.best_offer.delta_vs_target_percent >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {tracker.best_offer.delta_vs_target_percent > 0 ? '+' : ''}{tracker.best_offer.delta_vs_target_percent?.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Closing Prob.</p>
                        <p className="font-bold">{tracker.best_offer.probability_of_closing}%</p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground italic">
                      {tracker.best_offer.recommendation_reason}
                    </p>
                    
                    {tracker.best_offer.negotiation_target && (
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-sm font-medium">Counter at: {formatCurrency(tracker.best_offer.negotiation_target)}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <EmptyState
                    icon={Handshake}
                    title="Aucune offre"
                    description="Enregistrez les offres reçues pour obtenir une analyse."
                  />
                )}
              </CardContent>
            </Card>

            {/* Time Impact */}
            <Card data-testid="time-impact-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Timer size={18} />
                  Analyse Impact Temporel
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tracker.time_impact ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Coût de Détention Mensuel</p>
                        <p className="text-xl font-bold font-tabular text-red-600">
                          -{formatCurrency(tracker.time_impact.monthly_holding_cost)}/mois
                        </p>
                      </div>
                      <Badge className={cn('capitalize', URGENCY_COLORS[tracker.time_impact.urgency_level])}>
                        Urgence {tracker.time_impact.urgency_level === 'high' ? 'Élevée' : 
                                 tracker.time_impact.urgency_level === 'medium' ? 'Moyenne' : 'Basse'}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      {tracker.time_impact.urgency_reason}
                    </p>
                    
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/30">
                          <TableHead>Attente</TableHead>
                          <TableHead className="text-right">Coût</TableHead>
                          <TableHead className="text-right">Impact ROI</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>+30 jours</TableCell>
                          <TableCell className="text-right font-tabular text-red-600">
                            -{formatCurrency(tracker.time_impact.cost_if_wait_30_days)}
                          </TableCell>
                          <TableCell className="text-right font-tabular">
                            {tracker.time_impact.roi_after_30_days?.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>+60 jours</TableCell>
                          <TableCell className="text-right font-tabular text-red-600">
                            -{formatCurrency(tracker.time_impact.cost_if_wait_60_days)}
                          </TableCell>
                          <TableCell className="text-right font-tabular">
                            {tracker.time_impact.roi_after_60_days?.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>+90 jours</TableCell>
                          <TableCell className="text-right font-tabular text-red-600">
                            -{formatCurrency(tracker.time_impact.cost_if_wait_90_days)}
                          </TableCell>
                          <TableCell className="text-right font-tabular">
                            {tracker.time_impact.roi_after_90_days?.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-muted-foreground">Aucune donnée d'impact temporel disponible</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Scenario Outcomes */}
          {tracker.scenario_outcomes && Object.keys(tracker.scenario_outcomes).length > 0 && (
            <Card data-testid="scenario-outcomes">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Scales size={18} />
                  Comparaison des Scénarios
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(tracker.scenario_outcomes).map(([key, scenario]) => (
                    <div 
                      key={key} 
                      className={cn(
                        "p-4 rounded-lg border",
                        key === 'base_case' && "border-[#3A4A40] bg-[#F8FAF9]"
                      )}
                    >
                      <p className="text-xs text-muted-foreground uppercase">{scenario.scenario_name}</p>
                      <p className="text-lg font-bold font-tabular mt-1">
                        {formatCurrency(scenario.exit_price)}
                      </p>
                      <div className="flex justify-between mt-2 text-sm">
                        <span className="text-muted-foreground">Profit Net</span>
                        <span className={cn(
                          "font-tabular font-medium",
                          scenario.net_profit >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {formatCurrency(scenario.net_profit)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">ROI</span>
                        <span className="font-medium">{scenario.roi_percent?.toFixed(1)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers" className="space-y-4">
          <Card data-testid="offers-table">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle>Toutes les Offres</CardTitle>
                <Button size="sm" onClick={() => setShowAddOffer(true)}>
                  <Plus size={16} className="mr-1" />
                  Ajouter Offre
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {tracker.offers?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/30">
                      <TableHead>Prix</TableHead>
                      <TableHead>Profit Net</TableHead>
                      <TableHead>ROI</TableHead>
                      <TableHead>vs Cible</TableHead>
                      <TableHead>Conditions</TableHead>
                      <TableHead>Prob.</TableHead>
                      <TableHead>Recommandation</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tracker.offers.map((offer) => (
                      <TableRow key={offer.offer_id}>
                        <TableCell className="font-bold font-tabular">
                          {formatCurrency(offer.offer_price)}
                        </TableCell>
                        <TableCell className={cn(
                          "font-tabular",
                          offer.net_profit >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {formatCurrency(offer.net_profit)}
                        </TableCell>
                        <TableCell className="font-tabular">{offer.roi_percent?.toFixed(1)}%</TableCell>
                        <TableCell className={cn(
                          "font-tabular",
                          offer.delta_vs_target_percent >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {offer.delta_vs_target_percent > 0 ? '+' : ''}{offer.delta_vs_target_percent?.toFixed(1)}%
                        </TableCell>
                        <TableCell className="capitalize">{offer.conditions}</TableCell>
                        <TableCell>{offer.probability_of_closing}%</TableCell>
                        <TableCell>
                          <Badge className={cn("capitalize", RECOMMENDATION_COLORS[offer.recommendation])}>
                            {offer.recommendation === 'accept' ? 'Accepter' :
                             offer.recommendation === 'negotiate' ? 'Négocier' :
                             offer.recommendation === 'reject' ? 'Refuser' : offer.recommendation}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            {offer.recommendation === 'accept' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0 text-emerald-600"
                                onClick={() => handleAcceptOffer(offer.offer_id)}
                                title="Accepter"
                              >
                                <CheckCircle size={16} />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0 text-red-600"
                              onClick={() => handleRejectOffer(offer.offer_id)}
                              title="Refuser"
                            >
                              <X size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState
                  icon={Handshake}
                  title="Aucune offre enregistrée"
                  description="Ajoutez les offres reçues pour les suivre et les analyser."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Analysis Tab */}
        <TabsContent value="pricing" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pricing Position */}
            <Card data-testid="pricing-position">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target size={18} />
                  Position Prix
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tracker.pricing_position ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Prix Demandé Actuel</p>
                        <p className="text-2xl font-bold font-tabular">
                          {formatCurrency(tracker.pricing_position.current_asking_price)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatPricePerSqm(tracker.pricing_position.current_asking_per_sqm)}
                        </p>
                      </div>
                      <Badge 
                        className={cn(
                          "capitalize text-sm px-3 py-1",
                          tracker.pricing_position.position_label === 'correctly_priced' && "bg-emerald-100 text-emerald-700",
                          tracker.pricing_position.position_label === 'underpriced' && "bg-blue-100 text-blue-700",
                          tracker.pricing_position.position_label === 'overpriced' && "bg-amber-100 text-amber-700",
                          tracker.pricing_position.position_label === 'significantly_overpriced' && "bg-red-100 text-red-700"
                        )}
                      >
                        {tracker.pricing_position.position_label?.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="p-3 bg-secondary/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Comp-Supported Range</p>
                      <div className="flex justify-between text-sm">
                        <span>Low: {formatCurrency(tracker.pricing_position.comp_range_low)}</span>
                        <span className="font-medium">Base: {formatCurrency(tracker.pricing_position.comp_range_base)}</span>
                        <span>High: {formatCurrency(tracker.pricing_position.comp_range_high)}</span>
                      </div>
                    </div>
                    
                    <div className="text-center p-3 rounded-lg bg-secondary/20">
                      <p className={cn(
                        "text-2xl font-bold",
                        tracker.pricing_position.position_delta_percent > 0 ? "text-red-600" : 
                        tracker.pricing_position.position_delta_percent < -5 ? "text-blue-600" : "text-emerald-600"
                      )}>
                        {tracker.pricing_position.position_delta_percent > 0 ? '+' : ''}{tracker.pricing_position.position_delta_percent?.toFixed(1)}%
                      </p>
                      <p className="text-xs text-muted-foreground">vs Market Base</p>
                    </div>
                    
                    {tracker.pricing_position.adjustment_needed && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm font-medium text-amber-800">
                          Adjustment recommended: {tracker.pricing_position.adjustment_direction}
                        </p>
                        <p className="text-sm text-amber-700">
                          Suggested reduction: {formatCurrency(tracker.pricing_position.suggested_adjustment_amount)}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Recommended Listing Range</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 bg-secondary/30 rounded">
                          <p className="text-xs text-muted-foreground">Low</p>
                          <p className="font-tabular font-medium">{formatCurrency(tracker.pricing_position.recommended_price_low)}</p>
                        </div>
                        <div className="p-2 bg-[#E8F5E9] rounded border border-emerald-200">
                          <p className="text-xs text-muted-foreground">Optimal</p>
                          <p className="font-tabular font-medium">{formatCurrency(tracker.pricing_position.recommended_price_base)}</p>
                        </div>
                        <div className="p-2 bg-secondary/30 rounded">
                          <p className="text-xs text-muted-foreground">High</p>
                          <p className="font-tabular font-medium">{formatCurrency(tracker.pricing_position.recommended_price_high)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    icon={ChartLine}
                    title="No pricing analysis"
                    description="Ajoutez des données comparables pour l'analyse des prix."
                  />
                )}
              </CardContent>
            </Card>

            {/* Key Price Levels */}
            <Card data-testid="key-prices">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CurrencyEur size={18} />
                  Key Price Levels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Current Asking</p>
                      <p className="text-xs text-muted-foreground">Active listing price</p>
                    </div>
                    <p className="text-xl font-bold font-tabular">
                      {formatCurrency(tracker.current_asking_price || tracker.target_exit_price)}
                    </p>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Target Price</p>
                      <p className="text-xs text-emerald-600">Desired exit price</p>
                    </div>
                    <p className="text-xl font-bold font-tabular text-emerald-700">
                      {formatCurrency(tracker.target_exit_price)}
                    </p>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div>
                      <p className="text-sm font-medium text-amber-800">Minimum Acceptable</p>
                      <p className="text-xs text-amber-600">Walk-away threshold</p>
                    </div>
                    <p className="text-xl font-bold font-tabular text-amber-700">
                      {formatCurrency(tracker.minimum_acceptable_price)}
                    </p>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <p className="text-sm font-medium text-red-800">Break-Even</p>
                      <p className="text-xs text-red-600">No profit, no loss</p>
                    </div>
                    <p className="text-xl font-bold font-tabular text-red-700">
                      {formatCurrency(tracker.break_even_price)}
                    </p>
                  </div>
                  
                  {tracker.comp_supported_range && (
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <p className="text-sm font-medium text-blue-800">Comp-Supported</p>
                        <p className="text-xs text-blue-600">Market-based value</p>
                      </div>
                      <p className="text-xl font-bold font-tabular text-blue-700">
                        {formatCurrency(tracker.comp_supported_range.base)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-4">
          <Card data-testid="price-history">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock size={18} />
                Historique des Prix
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tracker.price_history?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/30">
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Old Price</TableHead>
                      <TableHead className="text-right">New Price</TableHead>
                      <TableHead className="text-right">Reduction</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tracker.price_history.map((pr, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDate(pr.date)}</TableCell>
                        <TableCell className="text-right font-tabular">
                          {formatCurrency(pr.old_price)}
                        </TableCell>
                        <TableCell className="text-right font-tabular">
                          {formatCurrency(pr.new_price)}
                        </TableCell>
                        <TableCell className="text-right font-tabular text-red-600">
                          -{pr.reduction_percent?.toFixed(1)}%
                        </TableCell>
                        <TableCell className="capitalize">{pr.reason?.replace('_', ' ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState
                  icon={Clock}
                  title="Aucun changement de prix"
                  description="Les réductions de prix seront suivies ici."
                />
              )}
            </CardContent>
          </Card>
          
          {/* Activity Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Eye size={18} />
                Activity Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-secondary/30 rounded-lg text-center">
                  <p className="text-3xl font-bold">{tracker.days_on_market}</p>
                  <p className="text-xs text-muted-foreground">Days on Market</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg text-center">
                  <p className="text-3xl font-bold">{tracker.viewings}</p>
                  <p className="text-xs text-muted-foreground">Viewings</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg text-center">
                  <p className="text-3xl font-bold">{tracker.offers_count}</p>
                  <p className="text-xs text-muted-foreground">Offers Received</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg text-center">
                  <p className="text-3xl font-bold">{tracker.time_since_last_price_change || '-'}</p>
                  <p className="text-xs text-muted-foreground">Days Since Price Change</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Offer Modal */}
      <Dialog open={showAddOffer} onOpenChange={setShowAddOffer}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record New Offer</DialogTitle>
            <DialogDescription>
              Enter the details of the offer received
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Offer Price (€) *</label>
              <Input
                type="number"
                value={offerForm.offer_price}
                onChange={(e) => setOfferForm({...offerForm, offer_price: e.target.value})}
                placeholder="1,800,000"
                data-testid="offer-price-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Buyer Type</label>
                <Select 
                  value={offerForm.buyer_type} 
                  onValueChange={(v) => setOfferForm({...offerForm, buyer_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="expat">Expat</SelectItem>
                    <SelectItem value="corporate">Corporate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Conditions</label>
                <Select 
                  value={offerForm.conditions} 
                  onValueChange={(v) => setOfferForm({...offerForm, conditions: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Cash)</SelectItem>
                    <SelectItem value="financing">Financing</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="sale_contingent">Sale Contingent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Probabilité de closing (%)</label>
                <Input
                  type="number"
                  value={offerForm.probability_of_closing}
                  onChange={(e) => setOfferForm({...offerForm, probability_of_closing: parseInt(e.target.value) || 70})}
                  min="0"
                  max="100"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Days to Close</label>
                <Input
                  type="number"
                  value={offerForm.expected_days_to_close}
                  onChange={(e) => setOfferForm({...offerForm, expected_days_to_close: parseInt(e.target.value) || 60})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <textarea
                value={offerForm.notes}
                onChange={(e) => setOfferForm({...offerForm, notes: e.target.value})}
                className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Notes supplémentaires sur l'offre..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddOffer(false)}>Annuler</Button>
              <Button 
                onClick={handleAddOffer} 
                disabled={saving || !offerForm.offer_price}
                className="bg-[#3A4A40] hover:bg-[#2E3B33]"
                data-testid="submit-offer-btn"
              >
                {saving ? <ArrowsClockwise size={16} className="animate-spin mr-1" /> : null}
                Enregistrer l'offre
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Reduction Modal */}
      <Dialog open={showPriceReduction} onOpenChange={setShowPriceReduction}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enregistrer une réduction de prix</DialogTitle>
            <DialogDescription>
              Prix actuel: {formatCurrency(tracker.current_asking_price || tracker.listing_price)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">New Price (€) *</label>
              <Input
                type="number"
                value={priceReductionForm.new_price}
                onChange={(e) => setPriceReductionForm({...priceReductionForm, new_price: e.target.value})}
                placeholder="1,750,000"
                data-testid="new-price-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Raison</label>
              <Select 
                value={priceReductionForm.reason} 
                onValueChange={(v) => setPriceReductionForm({...priceReductionForm, reason: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="market_feedback">Retour du marché</SelectItem>
                  <SelectItem value="time_on_market">Temps sur le marché</SelectItem>
                  <SelectItem value="comp_adjustment">Ajustement comparable</SelectItem>
                  <SelectItem value="buyer_feedback">Retour acheteur</SelectItem>
                  <SelectItem value="strategy_change">Changement de stratégie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowPriceReduction(false)}>Annuler</Button>
              <Button 
                onClick={handlePriceReduction} 
                disabled={saving || !priceReductionForm.new_price}
                className="bg-[#3A4A40] hover:bg-[#2E3B33]"
              >
                {saving ? <ArrowsClockwise size={16} className="animate-spin mr-1" /> : null}
                Enregistrer la réduction
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Listing Setup Modal */}
      <Dialog open={showListingSetup} onOpenChange={setShowListingSetup}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Setup Listing</DialogTitle>
            <DialogDescription>
              Configure the listing details for this property
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Listing Price (€) *</label>
                <Input
                  type="number"
                  value={listingForm.listing_price}
                  onChange={(e) => setListingForm({...listingForm, listing_price: e.target.value})}
                  placeholder="1,900,000"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Start Date</label>
                <Input
                  type="date"
                  value={listingForm.listing_start_date}
                  onChange={(e) => setListingForm({...listingForm, listing_start_date: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Minimum Acceptable Price (€)</label>
              <Input
                type="number"
                value={listingForm.minimum_acceptable_price}
                onChange={(e) => setListingForm({...listingForm, minimum_acceptable_price: e.target.value})}
                placeholder="1,700,000"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Marketing Channel</label>
                <Select 
                  value={listingForm.marketing_channel} 
                  onValueChange={(v) => setListingForm({...listingForm, marketing_channel: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agency">Agency</SelectItem>
                    <SelectItem value="off_market">Off-Market</SelectItem>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="auction">Auction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Staging Quality</label>
                <Select 
                  value={listingForm.staging_quality} 
                  onValueChange={(v) => setListingForm({...listingForm, staging_quality: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Nom de l'agence</label>
                <Input
                  value={listingForm.agency_name}
                  onChange={(e) => setListingForm({...listingForm, agency_name: e.target.value})}
                  placeholder="Barnes Paris"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Nom de l'agent</label>
                <Input
                  value={listingForm.agent_name}
                  onChange={(e) => setListingForm({...listingForm, agent_name: e.target.value})}
                  placeholder="Jean Dupont"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowListingSetup(false)}>Annuler</Button>
              <Button 
                onClick={handleListingSetup} 
                disabled={saving || !listingForm.listing_price}
                className="bg-[#3A4A40] hover:bg-[#2E3B33]"
              >
                {saving ? <ArrowsClockwise size={16} className="animate-spin mr-1" /> : null}
                Configurer l'annonce
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Page Help */}
      <PageHelpBlock 
        pageKey="exit-tracker" 
        content={pageExplanations.exitTracker} 
      />
    </div>
  );
};

export default ExitTracker;
